import { useDebouncedValue } from "@mantine/hooks"
import { buildKnowledgeSearchUrl, KNOWLEDGE_SEARCH_DEBOUNCE_MS, KNOWLEDGE_SEARCH_MIN_LENGTH, stripHighlightTags } from "../utils/knowledgeBase"
import useSWRImmutable from "swr/immutable"
import { useMemo } from "react"

type SearchResponse = {
  total: number
  results: {
    id: number
    url: string
    title: string
    category?: string
  }[]
}

export type KnowledgeArticle = {
  id: number
  url: string
  title: string
  category?: string
}

const fetcher = async (url: string): Promise<SearchResponse> => {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`ナレッジベース検索に失敗しました（${res.status}）`)
  return res.json()
}

export function useKnowledgeSearch(term: string) {
  const [debounceTerm] = useDebouncedValue(term, KNOWLEDGE_SEARCH_DEBOUNCE_MS)
  const rawTerm = term.trim()
  const fetchTerm = debounceTerm.trim()

  const hasEnoughInput = rawTerm.length >= KNOWLEDGE_SEARCH_MIN_LENGTH
  const shouldFetch = fetchTerm.length >= KNOWLEDGE_SEARCH_MIN_LENGTH
  const isDebouncing = rawTerm !== fetchTerm

  const { data, isValidating, error } = useSWRImmutable<SearchResponse>(
    shouldFetch ? buildKnowledgeSearchUrl(fetchTerm) : null,
    fetcher,
    { keepPreviousData: true, shouldRetryOnError: false },
  )

  const articles = useMemo<KnowledgeArticle[]>(() => {
    if (!hasEnoughInput || !shouldFetch) return []
    const results = data?.results
    if (!Array.isArray(results)) return []
    return results.map(r => ({
      id: r.id,
      url: r.url,
      title: stripHighlightTags(r.title),
      category: r.category,
    }))
  }, [hasEnoughInput, shouldFetch, data])

  const total = hasEnoughInput && shouldFetch ? (data?.total ?? 0) : 0

  return {
    articles,
    total,
    hasEnoughInput,
    isLoading: hasEnoughInput && (isDebouncing || isValidating),
    error: hasEnoughInput ? error : undefined,
  }
}
